import { defineStore } from 'pinia'

import { storage } from '../utils/storage'
import { ref, watch } from 'vue'
import type { ContentDisplaySettings } from '../composables/feature-flags/index.types'
import { contentDisplaySettingsSchema } from '../composables/feature-flags/index.types'
import { getConfigStorePath } from '../utils/getConfigStorePath'
/**
 * Key used to store feature flags in local storage.
 */
export const CONTENT_DISPLAY_SETTINGS_KEY = 'app:contentDisplaySettings'

/**
 * Expiration time for feature flags stored in local storage, in milliseconds.
 */
const CONTENT_DISPLAY_SETTINGS_EXPIRATION_MS = 60 * 1000 // 1 minute

/**
 * Parses the provided feature flags or returns the default feature flags if parsing fails.
 *
 * @param values - The raw feature flags data to parse.
 * @returns Parsed feature flags or default feature flags if parsing fails.
 */
function parseAndValidateContentSettings(
  values: unknown
): ContentDisplaySettings {
  const parsedData = contentDisplaySettingsSchema
    .passthrough()
    .safeParse(values)
  if (!parsedData.success) {
    throw new Error(`Invalid feature flags: ${parsedData.error}`)
  }
  return parsedData.data
}

/**
 * Generates an empty ContentDisplaySettings object.
 * @fixme this is all going away in the refactored and partitioned rework.
 * @returns An empty ContentDisplaySettings object.
 */
function generateEmptyContentDisplaySettings(): ContentDisplaySettings {
  return {
    // Populate with default values for all required properties
    // Example:
    sponsoredTiles: {
      provider: 'off',
      tilesCount: 0,
    },
    homePage: {
      doodle: 'off', // on or off or random
      design: 'msn', // google or msn
      searchboxDesign: 'v2', // default or v2 or v2-big-yolo or v3
    },
    summaryPage: {
      // design: 'msn', // google or msn
      siderail: {
        // position: 'full-height', // full-height or below-header
        // topClass: 'default_article_top_siderail',
        // middleClass: '',
        // bottomClass: '',
        topArticleGroup: {
          // design: 'google', // google or msn
          // articlesPerGroup: 3, // number of articles being displayed
          // type: 'related-stories', // top-stories or related-stories
        },
        bottomArticleGroup: {
          // design: 'google', // google or msn
          // articlesPerGroup: 3, // number of articles being displayed
          // type: 'related-stories', // top-stories or related-stories
        },
      },
    },
    // name: 'msn_inspirational_default',
    timestamp: new Date().toISOString(),
    ayData: {},
    adsDynamicFloorPricing: {
      isEnabled: false,
    },
  }
}
/**
 * Loads feature flags from local storage. If the stored flags are expired or invalid,
 * it returns the default feature flags.
 *
 * @returns The feature flags loaded from local storage or the default feature flags.
 */
function loadFromLocalStorage(): ContentDisplaySettings {
  const localStorageFeatureFlag =
    storage.getItem(CONTENT_DISPLAY_SETTINGS_KEY) ?? ''
  try {
    const { featureFlags, timestamp } = JSON.parse(localStorageFeatureFlag)
    const isExpired =
      Date.now() - timestamp > CONTENT_DISPLAY_SETTINGS_EXPIRATION_MS
    if (isExpired) {
      console.warn(
        'Feature flags in local storage are expired. Removing from local storage.'
      )
      storage.removeItem(CONTENT_DISPLAY_SETTINGS_KEY)
      return generateEmptyContentDisplaySettings()
    }
    return parseAndValidateContentSettings(featureFlags)
  } catch {
    // this should never happen
    console.warn('Failed to parse feature flags from local storage.')
    storage.removeItem(CONTENT_DISPLAY_SETTINGS_KEY)
    return generateEmptyContentDisplaySettings()
  }
}

/**
 * Saves the provided feature flags to local storage with a timestamp.
 *
 * @param featureFlags - The feature flags to save.
 */
function saveToLocalStorage(featureFlags: ContentDisplaySettings) {
  const featureFlagStr = JSON.stringify({
    featureFlags: parseAndValidateContentSettings(featureFlags),
    timestamp: Date.now(),
  })
  storage.setItem(CONTENT_DISPLAY_SETTINGS_KEY, featureFlagStr)
}

/**
 * Pinia store for managing feature flags. Provides reactive state for feature flags
 * and a method to load them from an API.
 */
export const useContentDisplaySettingsStore = defineStore(
  'contentDisplaySettings',
  () => {
    const featureFlags = ref<ContentDisplaySettings>(loadFromLocalStorage())
    const isLoading = ref(false)

    /**
     * Fetches feature flags from the API and updates the store and local storage.
     */
    async function loadContentDisplaySettings() {
      isLoading.value = true

      const configPath = await getConfigStorePath('contentDisplaySettings')
      try {
        const response = await fetch(configPath, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        })
        const data = await response.json()
        const parsedFlags = parseAndValidateContentSettings(data)
        featureFlags.value = parsedFlags
      } catch (error) {
        console.error('Error loading feature flags:', error)
      } finally {
        isLoading.value = false
      }
    }
    const initialized = loadContentDisplaySettings()
    const intervalId = setInterval(() => {
      loadContentDisplaySettings()
    }, CONTENT_DISPLAY_SETTINGS_EXPIRATION_MS * 2) // every 2 minutes

    window.addEventListener('beforeunload', () => {
      clearInterval(intervalId)
    })
    // Watches for changes in feature flags and saves them to local storage.
    watch(featureFlags, (newValue) => {
      if (newValue === null) {
        return
      }
      saveToLocalStorage(newValue)
    })

    return { featureFlags, isLoading, initialized, loadContentDisplaySettings }
  }
)
